import React, { useEffect, useRef, useState } from 'react';
import {
  LuArrowUp,
  LuTarget,
  LuRadar,
  LuUsers,
  LuWorkflow,
  LuRocket,
  LuEye,
  LuUserPlus,
  LuClock,
  LuMessageSquare,
  LuMail,
  LuCheck,
} from 'react-icons/lu';
import {
  PreviewRoot,
  PreviewInner,
  PreviewHeadline,
  PreviewCard,
  CardHeader,
  SessionLabel,
  LiveBadge,
  Conversation,
  BubbleRow,
  BubbleBody,
  Caret,
  Composer,
  Builder,
  Rail,
  RailStep,
  RailDot,
  RailLabel,
  RailLine,
  StageCanvas,
  Chips,
  Chip,
  ScanList,
  ScanRow,
  ScanTrack,
  ScanNote,
  LeadList,
  LeadRow,
  LeadAvatar,
  LeadName,
  ScoreTrack,
  ScoreValue,
  SequenceRow,
  SequenceStep,
  SequenceConnector,
  LaunchCard,
  LaunchIcon,
} from './AuthChatPreview.styles';

const script = [
  {
    role: 'user',
    text: 'Find 500 Series A SaaS founders in DACH hiring SDRs right now.',
  },
  {
    role: 'agent',
    text: 'Found 512 matches. 431 have verified emails and 486 are reachable on LinkedIn. Want me to enrich and score them?',
  },
  {
    role: 'user',
    text: 'Yes, then build a multi channel sequence: LinkedIn touch, then email day 2.',
  },
  {
    role: 'agent',
    text: 'Building your campaign now. Step 1, visit profile. Step 2, connection request. Step 3, wait 2 days. Step 4, personalised LinkedIn message. Step 5, email follow up.',
  },
  {
    role: 'user',
    text: 'Add one more email on day 7 and launch it Monday at 9:00.',
  },
  {
    role: 'agent',
    text: 'Sequence ready. 6 touches across LinkedIn and email, projected 14% reply rate. Campaign live on your Monday 9:00 window.',
  },
];

function useTypedScript() {
  const [turns, setTurns] = useState([]);
  const [typing, setTyping] = useState('');
  const [role, setRole] = useState('user');
  const [step, setStep] = useState(0);
  const idx = useRef(0);
  const char = useRef(0);

  useEffect(() => {
    let timer;

    const tick = () => {
      const current = script[idx.current % script.length];
      setRole(current.role);
      setStep(idx.current % script.length);

      if (char.current <= current.text.length) {
        setTyping(current.text.slice(0, char.current));
        char.current += 1;
        timer = setTimeout(tick, current.role === 'user' ? 33 : 18);
        return;
      }

      timer = setTimeout(() => {
        setTyping('');
        char.current = 0;
        idx.current += 1;
        if (idx.current % script.length === 0) {
          setTurns([]);
        } else {
          setTurns(prev => [...prev, current]);
        }
        tick();
      }, 1100);
    };

    tick();
    return () => clearTimeout(timer);
  }, []);

  return { turns, typing, role, step };
}

const STAGE_BY_STEP = ['icp', 'searching', 'leads', 'sequence', 'sequence', 'launch'];

const RAIL = [
  { id: 'icp', label: 'ICP', icon: LuTarget },
  { id: 'searching', label: 'Search', icon: LuRadar },
  { id: 'leads', label: 'Leads', icon: LuUsers },
  { id: 'sequence', label: 'Sequence', icon: LuWorkflow },
  { id: 'launch', label: 'Launch', icon: LuRocket },
];

const MINI_LEADS = [
  { name: 'Lena Brandt', role: 'CEO, Nuvora', score: 96 },
  { name: 'Tobias Frey', role: 'Founder, Sygnal', score: 91 },
  { name: 'Marie Kessler', role: 'CRO, Loopwise', score: 87 },
];

const MINI_SEQUENCE = [
  { icon: LuEye, label: 'Visit profile' },
  { icon: LuUserPlus, label: 'Connection request' },
  { icon: LuClock, label: 'Wait 2 days' },
  { icon: LuMessageSquare, label: 'LinkedIn message' },
  { icon: LuMail, label: 'Email follow up' },
];

function Bubble({ role, text, caret }) {
  const isUser = role === 'user';
  return (
    <BubbleRow $isUser={isUser}>
      <BubbleBody $isUser={isUser}>
        {text}
        {caret && <Caret />}
      </BubbleBody>
    </BubbleRow>
  );
}

function MiniBuilder({ stage }) {
  const activeIdx = RAIL.findIndex(r => r.id === stage);

  return (
    <Builder>
      <Rail>
        {RAIL.map((s, i) => {
          const Icon = s.icon;
          const done = i < activeIdx;
          const active = i === activeIdx;
          return (
            <RailStep key={s.id}>
              <RailDot $active={active} $done={done}>
                {done ? <LuCheck /> : <Icon />}
              </RailDot>
              <RailLabel $active={active}>{s.label}</RailLabel>
              {i < RAIL.length - 1 && <RailLine $done={done} />}
            </RailStep>
          );
        })}
      </Rail>

      <StageCanvas key={stage}>
        {stage === 'icp' && (
          <Chips>
            {['Series A', 'SaaS', 'DACH', 'Hiring SDRs', '50 to 200 staff'].map((chip, i) => (
              <Chip key={chip} style={{ animationDelay: `${i * 90}ms` }}>
                {chip}
              </Chip>
            ))}
          </Chips>
        )}

        {stage === 'searching' && (
          <ScanList>
            {['LinkedIn', 'Database', 'Company data'].map((src, i) => (
              <ScanRow key={src}>
                <span>{src}</span>
                <ScanTrack $delay={i * 0.2}>
                  <span />
                </ScanTrack>
              </ScanRow>
            ))}
            <ScanNote>Scanning sources for matching leads</ScanNote>
          </ScanList>
        )}

        {stage === 'leads' && (
          <LeadList>
            {MINI_LEADS.map((l, i) => (
              <LeadRow key={l.name} style={{ animationDelay: `${i * 140}ms` }}>
                <LeadAvatar>{l.name.charAt(0)}</LeadAvatar>
                <LeadName>
                  {l.name}
                  <span>{l.role}</span>
                </LeadName>
                <ScoreTrack>
                  <span style={{ width: `${l.score}%` }} />
                </ScoreTrack>
                <ScoreValue>{l.score}</ScoreValue>
              </LeadRow>
            ))}
          </LeadList>
        )}

        {stage === 'sequence' && (
          <SequenceRow>
            {MINI_SEQUENCE.map((s, i) => {
              const Icon = s.icon;
              return (
                <React.Fragment key={s.label}>
                  <SequenceStep style={{ animationDelay: `${i * 180}ms` }}>
                    <Icon />
                    <span>{s.label}</span>
                  </SequenceStep>
                  {i < MINI_SEQUENCE.length - 1 && <SequenceConnector />}
                </React.Fragment>
              );
            })}
          </SequenceRow>
        )}

        {stage === 'launch' && (
          <LaunchCard>
            <LaunchIcon>
              <span className="ping" />
              <LuRocket />
            </LaunchIcon>
            <div>
              <strong>Campaign live</strong>
              <p>6 touches, 512 leads, sending Monday from 9:00</p>
            </div>
          </LaunchCard>
        )}
      </StageCanvas>
    </Builder>
  );
}

function AuthChatPreview() {
  const { turns, typing, role, step } = useTypedScript();
  const stage = STAGE_BY_STEP[step] ?? 'icp';

  return (
    <PreviewRoot>
      <PreviewInner>
        <PreviewHeadline>
          Describe your buyer.
          <br />
          <span>Your agent creates the campaign.</span>
        </PreviewHeadline>

        <PreviewCard>
          <CardHeader>
            <SessionLabel>
              <span className="dot" />
              Campaign session
            </SessionLabel>
            <LiveBadge>
              <span className="pulse">
                <span className="ring" />
                <span className="core" />
              </span>
              Live
            </LiveBadge>
          </CardHeader>

          <Conversation>
            {turns.map((t, i) => (
              <Bubble key={`${t.role}-${i}`} role={t.role} text={t.text} />
            ))}
            {typing && <Bubble role={role} text={typing} caret />}
          </Conversation>

          {step >= 3 && <MiniBuilder stage={stage} />}

          <Composer>
            <div>
              <span className="placeholder">Ask your agent to build a campaign</span>
              <span className="send">
                <LuArrowUp />
              </span>
            </div>
          </Composer>
        </PreviewCard>
      </PreviewInner>
    </PreviewRoot>
  );
}

export default AuthChatPreview;
